/**
 * Which permission a page needs to be shown at all.
 *
 * Keys are route names (src/router/routes.js), values are catalogue keys the
 * API's Rbac.php defines. A page not listed here needs no permission beyond
 * being signed in (the home page, for instance). The API enforces the same
 * catalogue on its endpoints, so hiding a page here is a courtesy, not the
 * protection — the protection is the 403 the API answers with.
 */
export const PAGE_PERMISSION = {
  // Sales reports (our API and the external service alike: reading sales)
  analytics: "reports.sales",
  "analytics-daily": "reports.sales",
  "analytics-shift": "reports.sales",
  "analytics-weekly": "reports.sales",
  "analytics-monthly": "reports.sales",
  "analytics-custom": "reports.sales",
  "analytics-productmix": "reports.sales",
  "breakdown-size-sales": "reports.sales",
  "analytics-hourly-heatmap": "reports.sales",
  "breakdown-day-part": "reports.sales",
  "breakdown-channel-mix": "reports.sales",
  "analytics-online-orders": "reports.sales",
  "breakdown-delivery-heatmap": "reports.sales",
  "store-scorecard": "reports.sales",
  "store-benchmark": "reports.sales",
  "breakdown-menu-engineering": "reports.sales",
  "financials-avg-ticket": "reports.financial",
  "financials-discounts-voids": "reports.financial",
  "financials-payment-breakdown": "reports.financial",
  "financials-food-cost-report": "reports.financial",

  // Performance
  "analytics-hq-weekly": "reports.royalty",
  "store-dashboard": "reports.sales",
  "store-peak-times": "reports.sales",
  "deal-analytics": "reports.sales",
  "analytics-conversion": "reports.sales",
  "store-customer-insights": "reports.crm",

  // Finance
  "financials-royalty": "reports.royalty",

  // Marketing & CRM
  banners: "banners.view",
  "push-notifications": "notifications.view",
  sms: "sms.view",
  coupons: "coupons.view",
  "deals-dashboard": "deals.view",
  "deal-builder": "deals.edit",
  "intelligence-dashboard": "deals.view",
  "rule-builder": "deals.edit",
  "deal-offer-performance": "deals.view",
  "cross-sell-manager": "deals.view",
  "loyalty-dashboard": "loyalty.view",
  "ab-test-manager": "deals.view",
  customers: "reports.crm",
  creations: "community.view",
  "crm-acquisition": "reports.crm",
  "crm-cohorts": "reports.crm",
  "crm-lapsed": "reports.crm",
  "crm-campaign-performance": "reports.crm",
  "marketing-utm-links": "reports.crm",
  marketing: "banners.view",
  email: "sms.view",
  "flash-deals": "deals.view",
  "gift-cards": "coupons.view",
  signups: "reports.crm",
  pipeline: "reports.crm",
  segments: "reports.crm",
  cases: "reports.crm",
  automation: "deals.view",

  // Menu & products
  menus: "menus.view",
  products: "products.view",
  featured: "menus.view",
  "option-items": "modifiers.view",

  // Operations
  "live-orders": "orders.view",
  "group-orders": "orders.view",
  dispatch: "orders.view",
  "food-cost": "inventory.view",
  pnl: "reports.financial",
  "vision-ai": "orders.view",
  "voice-ai": "orders.view",
  "stock-month-report": "inventory.view",
  "stock-onhand": "inventory.view",
  "stock-overview": "inventory.view",
  "stock-count": "inventory.view",
  "stock-deliveries": "inventory.view",
  "stock-waste": "inventory.view",
  "stock-reorder": "inventory.view",
  "stock-list": "inventory.view",
  stocks: "inventory.view",
  "stock-audits": "inventory.view",
  "stock-reducted-reports": "inventory.view",
  "stock-added-reports": "inventory.view",
  vendors: "inventory.view",
  invoices: "inventory.view",

  // Settings
  roles: "users.view",
  "role-edit": "users.view",
  "panel-users": "users.view",
  "working-hours": "stores.view",
  // Editing is all the map does (store/addarea and the rest need stores.edit), so
  // someone who may only view stores, a store owner for one, does not get it.
  "delivery-map": "stores.edit",
  "pos-users": "posusers.view",
  settings: "loyalty.view",

  // Atlas panels: not routes, opened signed in as the same person (src/utils/atlasSso.js)
  "atlas-sms": "atlas.sms",
  "atlas-ai": "atlas.ai",
};

/** The permission a route needs, or null. */
export function pagePermission(routeName) {
  return PAGE_PERMISSION[routeName] || null;
}
